// @flow
import * as React from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import AnnotationUtil from '../../queries/annotations/AnnotationUtil';
import CommentUtil from '../../util/CommentUtil';
import ThumbnailUtil from '../../util/ThumbnailUtil';
import Action from '../../common/Action';
import Authenticator from '../../protocol/Authenticator';
import { toSMPTE } from '../../util/VideoUtil';
import InjectPropsFromGS from '../../hoc/InjectPropsFromGS';
import Div from '../../common/elements/Div';
import CommentReplies from './CommentReplies';
import Button from '../../common/Button';
import { AnnotationThread, CollectionUserEntry } from '../../autogenerated/ZoomBridge'; // eslint-disable-line
import TextareaSuggestions from '../../hoc/TextareaSuggestions';
import StringUtil from '../../util/StringUtil';
import AtMentionUserNotification from '../../components/comments/AtMentionUserNotification';
import Dropdown from '../../common/Dropdown';
import StoreListener from '../../store/StoreListener';
import { COLLECTION_USERS, PERMISSIONS, FULL_TIME_FORMAT, COMMENTS } from '../../store/Constants';
import type { Permissions } from '../../types';
import { WAVEPLAYER_DRAWING } from '../../util/Constants';
import { ThemeSetup } from '../../themes/ThemeSetup';

import './Comment2.css';
import { isMobile } from '../../util/ScreenUtil';
import ShowMoreText from './ShowMoreText';
import t from '../../i18n/I18n';
import Store from '../../store/Store';

const debug = require('../../debug/debug')('wc:comments:Comment2');
const MARKER_OFFSET = 16;

type Props = {
  comment: AnnotationThread,
  video: Object,
  style?: Object,
  isActive: boolean,
  isAudioOrVideo: boolean,
  showCommentIndex: boolean,
  PERMISSIONS: Permissions,
  onClick: () => void,
  onCancelClick: () => void,
  onSaveCommentClick: () => void,
  onDeleteCommentClick: () => void,
  onSaveReplyClick: () => void,
  onDeleteReplyClick: () => void,
  onResolveChange: () => void,
  ...textareaSuggestionsPropFlowTypes,
  onEditing: Function
};

type State = {
  showReplies: boolean,
  showReplyForm: boolean,
  isEditing: boolean,
  didRepliesExpand: boolean,
  didRepliesCollapse: boolean,
  willRepliesCollapse: boolean,
  creatorFullName: ?string,
  commentText: string,
  replyText: string,
  atMentions: string[],
  fullTimeFormat: Boolean
};

const getTextarea = (component) => component._replyTextarea || component._commentTextarea;
const getSuggestions = (component) => component.getSuggestions();
const getAcceptSuggestion = (component) => component.acceptSuggestion;
const storeItems = [COMMENTS, PERMISSIONS, FULL_TIME_FORMAT];

@StoreListener(storeItems)
@InjectPropsFromGS(['video'], 1000, true)
@TextareaSuggestions(getTextarea, getSuggestions, getAcceptSuggestion)
export default class Comment extends React.PureComponent<Props, State> {
  suggestions: Object[];
  _node: ?HTMLElement;
  _commentTextarea: ?HTMLTextAreaElement;
  _replyTextarea: ?HTMLTextAreaElement;
  kindOptions: Array<any>;

  static defaultProps = {
    showCommentIndex: true,
    style: {}
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      showReplies: this.getReplyCount() > 0,
      showReplyForm: false,
      isEditing: !this.props.comment.getThreadId(),
      didRepliesExpand: false,
      didRepliesCollapse: false,
      willRepliesCollapse: false,
      creatorFullName: this.getCreator(props),
      commentText: '',
      replyText: '',
      atMentions: [],
      showReplyButton: true,
      fullTimeFormat: false,
      isCreator: false,
      changedCommentKind: ''
    };

    this.onReplyClick = this.onReplyClick.bind(this);
    this.fetchCreatorFullname(props);
  }

  componentDidMount() {
    this.isCreator();
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.comment.getThreadId() !== this.props.comment.getThreadId()) {
      this.setState({ creatorFullName: this.getCreator(nextProps) });
      this.fetchCreatorFullname(nextProps);
    }
    if (this.getReplyCount(this.props) === 0 && this.getReplyCount(nextProps) === 1) {
      this.setState({ showReplies: true });
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (!prevState.showReplies && this.state.showReplies) {
      setTimeout(() => this.setState({ didRepliesExpand: true }), 200);
    }
    if (!prevState.willRepliesCollapse && this.state.willRepliesCollapse) {
      setTimeout(() => this.setState({ didRepliesCollapse: true }), 200);
      setTimeout(
        () =>
          this.setState({
            showReplies: false,
            didRepliesExpand: false,
            didRepliesCollapse: false,
            willRepliesCollapse: false
          }),
        400
      );
    }
  }

  getReplyCount = (props: Props = this.props) => {
    try {
      return props.comment.getAnnotation().getReplies().length;
    } catch (err) {
      return 0;
    }
  };

  getCreator(props: Props = this.props) {
    if (!props.comment) {
      return null;
    }

    return AnnotationUtil.getRootAnnotation(props.comment).getCreator();
  }

  fetchCreatorFullname = async (props: Props) => {
    const creator = this.getCreator(props);
    if (!creator) {
      return;
    }

    try {
      const creatorFullName = await ThumbnailUtil.loadUserFullName(creator);
      this.setState({ creatorFullName });
    } catch (err) {
      this.setState({ creatorFullName: 'Anonymous' });
    }
  };

  getSuggestions = () => {
    const suggestions = (this.suggestions = Store.get(COLLECTION_USERS, []).map((u: CollectionUserEntry) => ({
      value: u._userId,
      label: u._displayName || u._email,
      icon: ThumbnailUtil.avatar(u._userId)
    })));

    return suggestions;
  };

  acceptSuggestion = (suggestion: Object, text: string) => {
    const atMentions = this.getMentionedUsers(text);
    const nextState = { atMentions };
    if (this.state.isEditing) {
      nextState.commentText = text;
    } else {
      nextState.replyText = text;
    }

    this.setState(nextState);
  };

  getMentionedUsers(comment: string) {
    const usersArray = StringUtil.parseTextForAtMentions(comment, this.suggestions);
    const atMentions = this.suggestions.filter((user) => usersArray.indexOf(user.label) > -1);
    return atMentions;
  }

  scrollToLastReply = () => {
    if (!this._node) {
      return;
    }

    const container = this._node.querySelector('[name="CommentReplies"]');
    if (!container) {
      return;
    }

    const lastChild = container.lastChild;
    if (lastChild && lastChild.scrollIntoViewIfNeeded) {
      lastChild.scrollIntoViewIfNeeded();
    }
  };

  onClick = () => {
    const { comment, onClick } = this.props;
    if (!this.state.showReplies && this.getReplyCount() > 0) {
      this.setState({ showReplies: true });
    }

    onClick(comment);
  };

  getLastCommentReply = (comment: AnnotationThread) => {
    if (comment) {
      const replies: Array<AnnotationThread> = AnnotationUtil.getReplies(comment);
      if (replies) {
        const count = this.getReplyCount();
        return replies[count - 1];
      } else null;
    }
  };

  markAllRepliesAsRead = (comment: AnnotationThread) => {
    if (comment) {
      const replies: Array<AnnotationThread> = AnnotationUtil.getReplies(comment);
      replies.forEach((reply) => {
        const isUnread = CommentUtil.isUnread(comment, reply);
        if (isUnread) {
          CommentUtil.markCommentAsReadIfNeeded(comment, true);
        }
      });
    }
  };

  onReplyClick(comment: AnnotationThread) {
    const lastReply = this.getLastCommentReply(comment);
    if (CommentUtil.isUnread(comment, lastReply)) {
      CommentUtil.markCommentAsReadIfNeeded(comment, true);
    }
    this.setState({ showReplyForm: true });
  }

  onEditClick = () => {
    this.setState({
      isEditing: true,
      commentText: CommentUtil.getRootCommentText(this.props.comment)
    });
    //this.props.onEditing();
  };

  onDeleteClick = () => {
    const { comment, onDeleteCommentClick } = this.props;
    onDeleteCommentClick(comment);
  };

  onCancelClick = () => {
    const { onCancelClick } = this.props;
    if (onCancelClick) {
      onCancelClick();
    } else {
      this.setState({ isEditing: false });
    }
  };

  onSaveCommentClick = async () => {
    try {
      const { comment, onSaveCommentClick } = this.props;
      const { commentText } = this.state;
      if (!commentText) {
        return;
      }
      const updatedComment: AnnotationThread = _.cloneDeep(comment);
      if (
        updatedComment.getAnnotation().getText() != commentText ||
        (this.state.changedCommentKind && this.state.changedCommentKind !== comment.getAnnotationKind())
      ) {
        if (this.state.changedCommentKind && this.state.changedCommentKind !== comment.getAnnotationKind()) {
          updatedComment.setAnnotationKind(this.state.changedCommentKind);
        }
        AnnotationUtil.getRootAnnotation(updatedComment)
          .setText(commentText)
          .setCTime(Date.now());

        await onSaveCommentClick(updatedComment);
      }
      this.setState({ isEditing: false, commentText: '' });
    } catch (err) {
      debug('[onSaveCommentClick()] error: ', err);
    }
  };

  onSaveNewReplyClick = async () => {
    const { comment, onSaveCommentClick } = this.props;
    const { replyText } = this.state;
    const updatedComment = _.cloneDeep(comment);
    AnnotationUtil.appendNewReply(updatedComment, replyText);
    await onSaveCommentClick(updatedComment);
    if (this._node) {
      this.setState({ showReplyForm: false, replyText: '', showReplies: true });
      setTimeout(this.scrollToLastReply, 250);
    }
  };

  onResolveClick = (e: Event) => {
    e.stopPropagation();
    const { comment, onResolveChange } = this.props;
    this.markAllRepliesAsRead(comment);
    if (CommentUtil.isUnread(comment)) CommentUtil.markCommentAsReadIfNeeded(comment, true);
    onResolveChange(comment);
  };

  onKindChange = (kind: string) => {
    this.setState({ changedCommentKind: kind });
    //this.props.comment.setAnnotationKind(kind);
    //this.forceUpdate();
  };

  onCommentTextChange = (e: Event) => {
    this.setState({ commentText: e.target.value });
    this.props.onTextChange(e);
  };

  onReplyTextChange = (e: Event) => {
    this.setState({ replyText: e.target.value });
    this.props.onTextChange(e);
  };

  renderTimecode = () => {
    const { comment, video } = this.props;
    const hasTimeCode =
      comment.getAnnotationLocation().getSpatialLocation() || comment.getAnnotationLocation().getTemporalLocation();
    const millis = AnnotationUtil.getVideOffsetInMilliseconds(comment);
    const { frameRate } = video;
    return hasTimeCode ? (
      <div className="timecode">
        <div className="fa fa-clock-o" />
        <Div title={t('TIMECODE_OF_COMMENT')}>{toSMPTE(millis, frameRate)}</Div>
      </div>
    ) : null;
  };

  onEditStart = () => {
    this.setState({
      showReplyButton: !this.state.showReplyButton
    });
  };

  toggleShowReplies = () => {
    this.setState({ showReplies: !this.state.showReplies });
  };

  rander360Icon = () => {
    const { comment } = this.props;
    if (comment._annotation._attachments.length) {
      const commentType = comment._annotation._attachments[0];
      if (commentType.getFileName() === WAVEPLAYER_DRAWING) {
        return (
          <div className={'comment360Icon'}>
            <img style={{ marginTop: '-5px' }} src="images/waveplayer/svg/360_spherical_gray.svg" />
          </div>
        );
      }
    }
    return null;
  };

  isCreator = () => {
    const isCreator: boolean = CommentUtil.isCreator(this.props.comment);
    if (isCreator) {
      this.setState({ isCreator: true });
    }
  };

  getCommentString = (text) => {
    return text.split('\n').map((l, i, arr) => {
      const line = <span key={i}>{l}</span>;
      if (i === arr.length - 1) {
        return line;
      } else {
        return [line, <br key={i + 'br'} />];
      }
    });
  }

  renderDetail = () => {
    const { comment, isActive, isAudioOrVideo, showCommentIndex, onSaveReplyClick, onDeleteReplyClick } = this.props;

    const {
      showReplies,
      showReplyForm,
      creatorFullName,
      replyText,
      atMentions,
      didRepliesExpand,
      didRepliesCollapse,
      willRepliesCollapse,
      showReplyButton,
      fullTimeFormat,
      isCreator
    } = this.state;

    const { canResolveComments, canWriteComments, isAdmin } = this.props[PERMISSIONS];

    const creator = AnnotationUtil.getRootAnnotation(comment).getCreator();
    const allowEditActions = (canWriteComments && isCreator) || isAdmin;

    const commentIdx = comment.getAnnotationLocation().getCommentIndex();
    const replyCount = this.getReplyCount();
    const isPrivateNote = AnnotationUtil.isPrivateNote(comment);
    const isResolvedComment = AnnotationUtil.isResolved(comment);
    const opacity = CommentUtil.isUnread(comment) ? 1 : 0;
    const unreadMarker = (
      <div className="unreadMarker" style={{ opacity }}>
        {t('NEW')}
      </div>
    );
    let totalRepliesCount = comment.getAnnotation().getReplies() ? comment.getAnnotation().getReplies().length : null;
    return (
      <div>
        <div className="comment-body" onClick={this.onClick}>
          <div className="flex-row">
            <div className="flex-col left">
              <div className="avatar-wrapper">
                {unreadMarker}
                <div className="avatar" style={{ backgroundImage: `url(${ThumbnailUtil.avatar(creator)})` }} />
                {showCommentIndex ? (
                  <div
                    className={classNames('index', {
                      active: isActive
                    })}
                    style={{ background: CommentUtil.commentBackgroundByKind(comment) }}>
                    {commentIdx}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex-col flex-main">
              <div className="flex-row align-center spc-between">
                <Div className="author" title={creatorFullName}>
                  {creatorFullName}
                </Div>
                <Action custom predicates={[() => canResolveComments && !isPrivateNote]}>
                  <Button
                    className="btn-resolve"
                    text={t('MARK_AS_RESOLVED')}
                    icon={
                      comment.isResolved()
                        ? ThemeSetup.getImageURL('Icon_Checkmark.svg')
                        : ThemeSetup.getImageURL('Icon_Rectangle_Shape.svg')
                    }
                    onClick={this.onResolveClick}
                  />
                </Action>
              </div>
              <div className="info" onClick={CommentUtil.toggleTimeFormat}>
                <div>{CommentUtil.drawEditedOrModified(comment)}</div>
                {isAudioOrVideo ? this.rander360Icon() : null}
              </div>
              <div className="text">
                <ShowMoreText lines={4}>
                  <p className="textparagraph">{this.getCommentString(CommentUtil.getRootCommentText(comment))}</p>
                </ShowMoreText>
              </div>
              <Action custom predicates={[() => isAudioOrVideo]}>
                {this.renderTimecode()}
              </Action>
            </div>
          </div>
          <div className="flex-row align-center spc-between buttons">
            <div
              className={classNames({
                'left-buttons': !isMobile(),
                'left-buttons-mobile': isMobile()
              })}>
              <Action custom predicates={[() => allowEditActions && !isResolvedComment]}>
                <Button
                  className="btn-edit"
                  icon={ThemeSetup.getImageURL('edit_asset.svg')}
                  onClick={this.onEditClick}
                  title={t('EDIT_COMMENT')}
                  style={{ float: 'left', marginRight: '10px' }}
                />
              </Action>
              <Action custom predicates={[() => allowEditActions && !isResolvedComment]}>
                <Button
                  className="btn-remove"
                  icon={ThemeSetup.getImageURL('trash_red.svg')}
                  onClick={this.onDeleteClick}
                  title={t('DELETE_COMMENT')}
                />
              </Action>
            </div>
            <div>
              <Action
                custom
                predicates={[
                  () => canWriteComments && !isPrivateNote && !showReplyForm && !showReplies && showReplyButton
                ]}>
                <div className="flex-row align-center reply">
                  <Button type="CAPS" onClick={() => this.onReplyClick(comment)}>
                    {t('REPLY')}
                  </Button>
                </div>
              </Action>
            </div>
          </div>
        </div>
        <div>
          <div className="showRepliesToggle" onClick={this.toggleShowReplies}>
            {AnnotationUtil.getReplyCounts(this.props.comment) > 1 && (
              <div className="line">
                <div className="repliesSeparatorText">
                  {!showReplies ? (
                    <div>
                      {t('SHOW_REPLIES')} [+]({totalRepliesCount})
                    </div>
                  ) : (
                    <div>{t('HIDE_REPLIES')} [-]</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {showReplies ? (
          <div
            className={classNames('replies-container', {
              'did-expand': didRepliesExpand,
              'will-collapse': willRepliesCollapse,
              'did-collapse': didRepliesCollapse
            })}>
            {replyCount > 0 ? (
              <CommentReplies
                comment={comment}
                getCommentString={this.getCommentString}
                onEdit={onSaveReplyClick}
                onRemove={onDeleteReplyClick}
                onEditStart={this.onEditStart}
                onCloseRepliesClick={() =>
                  this.setState({
                    willRepliesCollapse: true,
                    didRepliesCollapse: false,
                    didRepliesExpand: false
                  })
                }
              />
            ) : null}
            <div className="flex-row justify-end buttons">
              <Action custom predicates={[() => canWriteComments && !isPrivateNote && !showReplyForm]}>
                <div className="flex-row align-center reply">
                  <Button type="CAPS" onClick={() => this.onReplyClick(comment)}>
                    {t('REPLY')}
                  </Button>
                </div>
              </Action>
            </div>
          </div>
        ) : null}

        {showReplyForm ? (
          <div>
            <textarea
              ref={(node) => (this._replyTextarea = node)}
              value={replyText}
              placeholder={StringUtil.toFirstUpper(t('REPLY').toLowerCase()) + '...'}
              autoFocus
              onChange={this.onReplyTextChange}
            />
            <AtMentionUserNotification atMentions={atMentions} />
            <div className="flex-row align-center spc-between buttons">
              <Button type="CAPS" className="save" onClick={this.onSaveNewReplyClick}>
                {t('SAVE')}
              </Button>
              <Button type="GRAY CAPS" onClick={() => this.setState({ showReplyForm: false, replyText: '' })}>
                {t('CANCEL')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  renderForm = () => {
    const { comment } = this.props;
    const { commentText, atMentions } = this.state;
    const kind = this.state.changedCommentKind ? this.state.changedCommentKind : comment.getAnnotationKind();
    const isNew = !comment.getThreadId();
    const title = `${isNew ? t('NEW_COMMENT') : t('EDIT_COMMENT_CAPS')}`;

    return (
      <div>
        <div className="flex-row align-center spc-between header">
          <div className="title">{title}</div>
          <Dropdown
            value={kind}
            options={AnnotationUtil.getTypesOfAnnotation()}
            anchor="RIGHT"
            onChange={this.onKindChange}
            listNodeName="Comment2_Portal"
            anchor='RIGHT'
            positions={{top: 38, right: 0, left: 'auto', bottom: 'auto'}}
          />
        </div>
        <textarea
          ref={(node) => (this._commentTextarea = node)}
          value={commentText}
          onChange={this.onCommentTextChange}
          autoFocus
        />
        <AtMentionUserNotification atMentions={atMentions} />
        <div className="flex-row align-center spc-between buttons">
          <Button type="CAPS" className="save" onClick={this.onSaveCommentClick}>
            {t('SAVE')}
          </Button>
          <Button type="GRAY CAPS" onClick={this.onCancelClick}>
            {t('CANCEL')}
          </Button>
        </div>
      </div>
    );
  };

  getMaxHeight = () => {
    let maxHeight = `${this.props.parentHeight - this.props.height - MARKER_OFFSET}px`;
    if (this.props.top) {
      maxHeight = `${this.props.height + MARKER_OFFSET}px`;
    }
    maxHeight = parseInt(maxHeight) > 400 || parseInt(maxHeight) < 0 ? '400px' : maxHeight;
    return maxHeight;
  };

  render() {
    const { style } = this.props;
    const { isEditing } = this.state;

    return (
      <div
        name="Comment2"
        ref={(node) => (this._node = node)}
        data-threadid={this.props.comment.getThreadId()}
        style={{ ...style, maxHeight: this.getMaxHeight() }}>
        {isEditing ? this.renderForm() : this.renderDetail()}
      </div>
    );
  }
}
